import { ArrowDown2 } from 'iconsax-reactjs';
import { cn } from '@/lib/utils';
import { useSymbol } from '@/lib/useSymbol';
import { PairIcon } from '@/components/PairIcon';
import { useI18n } from '@/lib/i18n';

/**
 * Trading pair selector for the top bar.
 *
 * A native <select> rather than a custom dropdown: it is keyboard accessible and
 * behaves correctly on touch without any work, and the styling that matters here
 * is the trigger, which can be styled freely with the select overlaid invisibly
 * on top of it.
 */
export function SymbolPicker() {
  const { symbol, available, setSymbol, isLoading } = useSymbol();
  const { t } = useI18n();

  if (isLoading) {
    return <span className="h-8 w-24 animate-pulse rounded-lg bg-muted" />;
  }

  if (available.length === 0) {
    return (
      <span className="rounded-lg bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
        No pairs
      </span>
    );
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-lg border border-border bg-elevated',
          'px-2.5 py-1.5 text-sm font-medium',
        )}
      >
        {symbol && <PairIcon symbol={symbol} size={18} />}
        <span className="tabular">{symbol ?? '—'}</span>
        <ArrowDown2 size={13} className="text-muted-foreground" />
      </div>

      <select
        aria-label={t('shell.tradingPair')}
        value={symbol ?? ''}
        onChange={(e) => setSymbol(e.target.value)}
        // Transparent and stretched over the trigger so the native picker opens
        // in the right place while the visible chrome stays ours.
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {available.map((i) => (
          <option key={i.id} value={i.symbol}>
            {i.symbol}
            {i.description ? ` — ${i.description}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
